<template lang="pug">
article.lfc.main-container-wide
  header.lfc-header(v-if="eyebrow || title || subtitle")
    p.lfc-eyebrow(v-if="eyebrow") {{ __(eyebrow) }}
    h1.lfc-title(v-if="title") {{ __(title) }}
    p.lfc-subtitle(v-if="subtitle") {{ __(subtitle) }}

  .lfc-body
    nav.lfc-nav(v-if="registeredSections.length" :aria-label="navLabel || 'Page sections'" ref="navEl")
      p.lfc-nav-label(v-if="navLabel") {{ __(navLabel) }}
      ul.lfc-nav-list
        li.lfc-nav-item(
          v-for="(section, i) in registeredSections"
          :key="section.id"
          :data-id="section.id"
        )
          a.lfc-nav-link(
            :href="`#${section.id}`"
            :aria-current="activeSectionId === section.id ? 'location' : undefined"
            :class="{ 'is-active': activeSectionId === section.id }"
            @click.prevent="scrollToSection(section.id)"
          )
            span.lfc-num(v-if="numbered" aria-hidden="true") {{ i + 1 }}.
            | {{ __(section.heading) }}

    main.lfc-main
      slot(name="sections")
</template>

<script setup lang="ts">
import { Comment, Fragment, Suspense, computed, onMounted, provide, ref, useSlots, watch, type Ref, type VNode } from 'vue'
import { useScrollSpy } from '../../js/useScrollSpy'

interface SectionMeta {
  id: string
  heading: string
}

const props = withDefaults(defineProps<{
  eyebrow?: string
  title?: string
  subtitle?: string
  numbered?: boolean
  navLabel?: string
}>(), {
  eyebrow  : '',
  title    : '',
  subtitle : '',
  navLabel : 'Contents',
})

// --- Slug / anchor id generation ---

function toSlug(text: string): string {
  return (text ?? '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
}

const slots = useSlots()

// Flattens the sections slot's vnodes so things that add a layer of
// indirection around the real LFCSection vnode don't hide it:
function flattenSectionVNodes(vnodes: VNode[]): VNode[] {
  const result: VNode[] = []

  for (const vnode of vnodes) {
    if (vnode.type === Comment) continue
    if (vnode.type === Fragment) {
      result.push(...flattenSectionVNodes((vnode.children as VNode[]) || []))
    } else if (vnode.type === Suspense) {
      const defaultSlot = (vnode.children as Record<string, unknown> | null)?.default
      const inner = typeof defaultSlot === 'function' ? (defaultSlot as () => VNode | VNode[])() : []
      result.push(...flattenSectionVNodes(Array.isArray(inner) ? inner : [inner]))
    } else {
      result.push(vnode)
    }
  }

  return result
}

const registeredSections = computed<SectionMeta[]>(() => {
  const counts = new Map<string, number>()

  return flattenSectionVNodes(slots.sections ? slots.sections() : []).map((vnode, i) => {
    const heading = (vnode.props?.heading as string) || ''
    const base = toSlug(heading) || `section-${i}`
    const count = counts.get(base) ?? 0
    counts.set(base, count + 1)

    return { id: count === 0 ? base : `${base}-${count + 1}`, heading }
  })
})

provide('lfc:registeredSections', registeredSections)
provide('lfc:numbered', computed(() => props.numbered ?? false))

let claimedCount = 0
provide('lfc:claimId', (): string | null => registeredSections.value[claimedCount++]?.id ?? null)

// --- Scroll spy ---
//
// Real elements only exist client-side, once a section actually mounts - kept
// separate from registeredSections so nav rendering never depends on that.
const sectionEls = ref<Map<string, HTMLElement>>(new Map())
provide('lfc:attachEl', (id: string, el: HTMLElement) => sectionEls.value.set(id, el))
provide('lfc:detachEl', (id: string) => sectionEls.value.delete(id))

const orderedSectionEls = computed(() => registeredSections.value
  .map((s) => sectionEls.value.get(s.id))
  .filter((el): el is HTMLElement => !!el))

const navEl = ref<HTMLElement | null>(null)

const { activeSectionId } = useScrollSpy(orderedSectionEls as Ref<HTMLElement[]>)

// When the active section changes, scroll the matching <li> into view within
// the nav — only when the nav itself is overflowing to avoid interfering with
// the page scroll triggered by scrollToSection.
watch(activeSectionId, (id) => {
  if (!id || !navEl.value) return
  if (navEl.value.scrollHeight <= navEl.value.clientHeight) return
  const item = navEl.value.querySelector<HTMLElement>(`[data-id="${id}"]`)
  item?.scrollIntoView({ block: 'nearest' })
})

function scrollToSection(id: string) {
  const el = document.getElementById(id)
  if (!el) return

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  el.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' })
}

onMounted(() => {
  if (!location.hash) return

  const navEntry = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined
  if (navEntry?.type === 'reload') return

  const id = location.hash.slice(1)
  if (!registeredSections.value.some((s) => s.id === id)) return

  // Set immediately so the nav highlights before the scroll animation completes
  activeSectionId.value = id
  scrollToSection(id)
})
</script>

<style lang="scss" scoped>
.lfc {
  @include mobile(){
    padding: 0 $space-m;
    margin: 0 auto;
  }
}

.lfc-header {
  margin: $space-m 0;
  padding: $space-xs 0;
}

.lfc-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.75rem;
  margin-bottom: $space-xs;
}

.lfc-title {
  margin-bottom: $space-xs;
  font-size: $size-xxxxl;
}

.lfc-subtitle {
  opacity: 0.6;
  margin-bottom: 0;
}

// Two-column layout: fixed-width nav + fluid body
.lfc-body {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: $space-xl;
  align-items: start;

  @include mobile() {
    display: block;
  }
}

// Sticky left nav — hidden entirely on mobile (no replacement, per product decision)
.lfc-nav {
  position: sticky;
  top: 1rem;
  align-self: start;
  max-height: 100vh;
  overflow-y: auto;

  @include mobile() {
    display: none;
  }
}

.lfc-nav-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  opacity: 0.5;
  margin-bottom: $space-xs;
}

.lfc-nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.lfc-nav-item {
  margin-bottom: $space-xxs;
}

.lfc-nav-link {
  display: block;
  padding: $space-xxs $space-xs;
  text-decoration: none;
  color: inherit;
  border-left: 2px solid transparent;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    color: currentColor;
    border-left-color: currentColor;
  }

  &.is-active {
    font-weight: 600;
    border-left-color: currentColor;
  }
}

.lfc-main {
  // Prevent grid blowout from wide content
  min-width: 0;
}

.lfc-num {
  font-variant-numeric: tabular-nums;
  margin-right: $space-xxs;
}
</style>
